import { CONDITIONS, SVC_MAP, PERMIT_RULES } from '../data/index.js';
import { getMarket } from '../data/markets.js';

export function calcPrice(grassSqFt, svcs, cond, lat, lng, profile) {
  const [mkt, mult] = getMarket(lat, lng);
  const c = CONDITIONS.find(x => x.id === cond) || CONDITIONS[0];

  // Tiered rate: covers mow + edge + trim + blow (full lawn maintenance visit)
  // Calibrated to real operator pricing across US markets
  let mowBase;
  if (grassSqFt <= 5000) {
    mowBase = 40 + (grassSqFt / 1000) * 3;
  } else if (grassSqFt <= 10000) {
    mowBase = 55 + ((grassSqFt - 5000) / 1000) * 4;
  } else if (grassSqFt <= 20000) {
    mowBase = 75 + ((grassSqFt - 10000) / 1000) * 2.5;
  } else {
    mowBase = 100 + ((grassSqFt - 20000) / 1000) * 1.5;
  }
  mowBase = Math.max(35, mowBase);

  const getAddon = (id) => {
    if (profile?.customPrices?.[id] !== undefined) return profile.customPrices[id];
    const s = SVC_MAP.get(id);
    return s ? s.addon : 0;
  };

  const recurringSvcs = svcs.filter(id => {
    const s = SVC_MAP.get(id);
    return s?.recurring;
  });
  const scopeSvcs = svcs.filter(id => {
    const s = SVC_MAP.get(id);
    return s && !s.recurring && !s.bidOnly;
  });
  const bidSvcs = svcs.filter(id => {
    const s = SVC_MAP.get(id);
    return s?.bidOnly;
  });

  // ── RECURRING rates ──
  let recurringAddons = 0;
  recurringSvcs.forEach(id => {
    if (id !== "maint") recurringAddons += getAddon(id);
  });
  const weeklyBase = (mowBase + recurringAddons) * mult;
  const wLow = Math.round(weeklyBase * 0.75 / 5) * 5;
  const wHigh = Math.round(weeklyBase * 1.35 / 5) * 5;
  const wMid = Math.round((wLow + wHigh) / 2 / 5) * 5;

  const bwMid = Math.round(wMid * 1.15 / 5) * 5;
  const bwLow = Math.round(wLow * 1.15 / 5) * 5;
  const bwHigh = Math.round(wHigh * 1.15 / 5) * 5;

  // ── THIS VISIT / SCOPE price ──
  let scopeAddons = 0;
  scopeSvcs.forEach(id => {
    scopeAddons += getAddon(id);
  });

  const condMowMult = c.mult * (cond === "negl" ? 1.4 : cond === "over" ? 1.25 : 1.0);
  const firstVisitMow = mowBase * condMowMult * mult;
  const firstVisitRecurring = recurringAddons * mult;
  const scopeTotal = firstVisitMow + firstVisitRecurring + (scopeAddons * mult);

  const scopeLow = Math.round(scopeTotal * 0.75 / 5) * 5;
  const scopeHigh = Math.round(scopeTotal * 1.35 / 5) * 5;
  const scopeMid = Math.round((scopeLow + scopeHigh) / 2 / 5) * 5;

  // ── Service breakdowns ──
  const recurringBreakdown = recurringSvcs.map(id => {
    const s = SVC_MAP.get(id);
    if (!s) return null;
    const basePrice = id === "maint" ? mowBase : getAddon(id);
    const price = Math.round(basePrice * mult);
    const isCustom = profile?.customPrices?.[id] !== undefined;
    return { id, label: s.label, price, basePrice, isCustom, type: "recurring" };
  }).filter(Boolean);

  const scopeBreakdown = scopeSvcs.map(id => {
    const s = SVC_MAP.get(id);
    if (!s) return null;
    const basePrice = getAddon(id);
    const price = Math.round(basePrice * mult);
    const isCustom = profile?.customPrices?.[id] !== undefined;
    return { id, label: s.label, price, basePrice, isCustom, type: "scope" };
  }).filter(Boolean);

  const bidBreakdown = bidSvcs.map(id => {
    const s = SVC_MAP.get(id);
    if (!s) return null;
    return { id, label: s.label, price: "Bid", bidOnly: true, type: "bid" };
  }).filter(Boolean);

  const breakdown = [...recurringBreakdown, ...scopeBreakdown, ...bidBreakdown];

  // Labor cost calculation
  let laborCost = null;
  let laborCostScope = null;
  let laborBreakdown = [];
  const activeMembers = (profile?.teamMembers || []).filter(m => m.active && m.rate > 0);
  if (activeMembers.length > 0) {
    const estHoursRecurring = Math.max(0.5, (grassSqFt / 1000) * 0.15);
    const estHoursScope = Math.max(0.75, (grassSqFt / 1000) * 0.15 * c.mult * 1.5);

    let totalRecurring = 0;
    let totalScope = 0;
    activeMembers.forEach(m => {
      let hourlyEquiv = 0;
      let perYard = 0;
      if (m.payType === "salary") {
        hourlyEquiv = m.rate / 2080;
      } else if (m.payType === "hourly") {
        hourlyEquiv = m.rate;
      } else if (m.payType === "peryard") {
        perYard = m.rate;
      }
      const memberRecurring = perYard > 0 ? perYard : Math.round(hourlyEquiv * estHoursRecurring * 100) / 100;
      const memberScope = perYard > 0 ? perYard : Math.round(hourlyEquiv * estHoursScope * 100) / 100;
      totalRecurring += memberRecurring;
      totalScope += memberScope;
      laborBreakdown.push({
        name: m.name,
        payType: m.payType,
        rate: m.rate,
        costRecurring: Math.round(memberRecurring),
        costScope: Math.round(memberScope),
      });
    });
    laborCost = Math.round(totalRecurring);
    laborCostScope = Math.round(totalScope);
  }

  // Per-job overhead
  let overheadPerJob = 0;
  if (profile?.expenses && profile?.estimatedMonthlyJobs > 0) {
    profile.expenses.forEach(exp => {
      if (exp.monthlyAmount) overheadPerJob += exp.monthlyAmount / profile.estimatedMonthlyJobs;
      if (exp.perJobAmount) overheadPerJob += exp.perJobAmount;
    });
    (profile.customExpenses || []).forEach(exp => {
      if (exp.monthlyAmount) overheadPerJob += exp.monthlyAmount / profile.estimatedMonthlyJobs;
      if (exp.perJobAmount) overheadPerJob += exp.perJobAmount;
    });
  }
  overheadPerJob = Math.round(overheadPerJob * 100) / 100;

  // Permit flags
  const permitFlags = [];
  PERMIT_RULES.forEach(rule => {
    if (rule.services.some(s => svcs.includes(s))) {
      permitFlags.push(rule);
    }
  });

  // Refer-out services
  const referOuts = [];
  if (profile?.servicesOffered) {
    svcs.forEach(id => {
      if (!profile.servicesOffered.includes(id)) {
        const s = SVC_MAP.get(id);
        if (s) referOuts.push(s);
      }
    });
  }

  // Strategy advice
  const hasScopeWork = scopeSvcs.length > 0 || cond !== "norm";
  let advice, strategyNote;
  if (!hasScopeWork) {
    advice = `Quote $${wMid}/week or $${bwMid}/biweekly. Lock in a recurring schedule on the first visit — that's where the money is.`;
    strategyNote = "Standard recurring — close on-site";
  } else if (cond === "norm") {
    advice = `This visit is $${scopeMid} for the one-time work, then present $${wMid}/week for ongoing maintenance. Bundle the scope work to close both at once.`;
    strategyNote = "Scope + recurring upsell";
  } else if (cond === "over") {
    advice = `Lead with $${scopeMid} for the cleanup and scope work today, then show $${wMid}/week for recurring maintenance. Frame it as "get it back to normal, then keep it there."`;
    strategyNote = "Cleanup + recurring close";
  } else {
    advice = `Don't discount the first visit — at $${scopeMid} you're already at the floor for this level of neglect. Hold firm. After cleanup, present the recurring rate at $${wMid}/week.`;
    strategyNote = "Hold firm on scope price";
  }

  return {
    wLow, wMid, wHigh,
    bwLow, bwMid, bwHigh,
    scopeLow, scopeMid, scopeHigh,
    hasScopeWork,
    breakdown, recurringBreakdown, scopeBreakdown, bidBreakdown,
    mkt, mult,
    advice, strategyNote,
    isCleanup: cond !== "norm",
    laborCost, laborCostScope, laborBreakdown,
    overheadPerJob,
    permitFlags,
    referOuts,
  };
}
